












































import {
  applyImageSize,
  cleanFromWysiwyg,
  expandPath,
  plainTextData,
} from "@/api/helpers";
import { Statement } from "@/api/models";
import { VueEditor } from "vue2-editor";
import { SiteState, SubmissionState } from "@/store/types";
import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { State } from "vuex-class";
import { emptyString, notEmptyString } from "../../api/validators";
import { customToolbar, editorOptions, textChange } from "../../settings/wysiwyg";
import { extractAllTags, highlightTags } from "@/api/expand-tags";
import FigureWidget from "./FigureWidget.vue";
import HelpWidget from "./HelpWidget.vue";
import { bus } from "@/main";

@Component({
  components: {
    VueEditor,
    FigureWidget,
    HelpWidget
  },
  filters: {},
})
export default class StatementField extends Vue {
  @Prop({ default: () => new Statement() }) statement: Statement;
  @Prop({ default: "biography" }) type: string;
  @Prop({ default: "Biography" }) label: string;
  @Prop({ default: "image" }) imagePrompt: string;
  @State("submission") submission: SubmissionState;
  @State("site") site: SiteState;

  text = "";
  alt = "";
  image = "";
  // filteredTags: Array<Tag> = [];
  imageLoaded = false;

  created() {
    this.sync();
    bus.$on("statement-image", ({ refKey, filename, alt }) => {
      if (refKey === this.type) {
        this.image = applyImageSize(filename, "thumb");
        if (!/^https?:/.test(this.image)) {
          this.imageLoaded = false;
          this.preloadImage();
        }
        if (notEmptyString(alt)) {
          this.alt = alt;
        }
      }
    });
    bus.$on("submission-loaded", () => {
      this.sync();
    });
  }

  sync() {
    this.text = notEmptyString(this.statement.text)
      ? extractAllTags(this.statement.text)
      : "";
    this.alt = this.statement.alt;
    this.image = this.statement.image;
    this.imageLoaded = notEmptyString(this.image, 8);
  }

  preloadImage() {
    const path = expandPath(this.image, 'thumb');
    const img = new Image();
    img.src = path;
    img.onload = () => {
      setTimeout(()=> {
        if (img.naturalWidth > 20) {
          this.imageLoaded = true;
          setTimeout(()=> {
            const imgTag = document.querySelector('.statement.' + this.containerRefClass + ' figure img');
            if (imgTag instanceof HTMLElement) {
              imgTag.click();
            }
            }, 500);
        }
      },500);
    };
    setTimeout(()=> {
      if (!this.imageLoaded) {
        this.preloadImage(); 
      }
    }, 1000);
  }

  get toolbar() {
    return customToolbar;
  }

  get editorOptions() {
    return editorOptions;
  }

  get textEditorId() {
    return [this.type, "text-editor"].join("-");
  }

   get maxDescriptionCharCount() {
    return 3000;
  }

  get altClasses() {
    const cls: string[] = [];
    if (this.statement.hasImage) {
      if (emptyString(this.alt)) {
        cls.push('error');
      }
    }
    return cls;
  }

  get textCounter() {
    const data = plainTextData(this.text)
    const wordsRef = "word count";
    return [data.chars, this.maxDescriptionCharCount].join(' / ') + ` (${wordsRef} ${data.words})`;
  }

  get numAltRows() {
    return this.isLandscape? 3 : 4;
  }

  get hasImage() {
    return notEmptyString(this.image, 8) && this.imageLoaded;
  }

  get idRef() {
    return ['statement-image', this.type].join('-');
  }

  get containerRefClass() {
    return this.type.replace(/_/g, "-");
  }

  get imagePlaceholderLabel() {
    return this.site.label("click_to_add_image").replace(/\s+image\b/, ' ' + this.imagePrompt.toLowerCase());
  }

  get layoutClass() {
    switch (this.type) {
      case 'biography':
        return 'landscape';
      default:
        return 'portrait';
    }
  }

  get isLandscape() {
    return this.layoutClass === 'landscape';
  }

  get wrapperClasses() {
    const cls = [this.containerRefClass, this.layoutClass];
    if (this.hasImage) {
      cls.push("has-image");
    }
    return cls;
  }

  getAspectRatio(): number {
    let aspect = 0;
    if (this.statement.hasImage && this.statement.attributes instanceof Object) {
      if (Object.keys(this.statement.attributes).length > 1) {
        const { height, width } = this.statement.attributes;
        if (height > 0 && width > 0) {
          aspect = width / height;
        }
      }
    }
    return aspect;
  }

  get targetAspect() {
    return this.layoutClass === 'portrait' ? 2/3 : 3/2;
  }

  get imageClasses() {
    const cls: string[] = [];
    const aspectRatio = this.getAspectRatio();
    if (aspectRatio > 0) {
      const orientation = aspectRatio < this.targetAspect ? 'too-tall' : 'too-wide';
      cls.push(orientation);
      const diff = this.targetAspect / aspectRatio;
      if (diff < 0.96 || diff > 1.04) {
        cls.push('cropped');
      }
    }
    return cls;
  }

  get midStyle() {
    const styles: any = {};
    const aspectRatio = this.getAspectRatio();
    if (aspectRatio > 0) {
      const sizeEl = document.querySelector('#' + this.idRef + ' .size-overlay');
      if (sizeEl instanceof HTMLElement) {
        const rect = sizeEl.getBoundingClientRect();
        if (aspectRatio < this.targetAspect) {
          styles.width = `${rect.width}px`;
        } else {
          styles.height = `${rect.height}px`;
        }
      }
    }
    return styles;
  }

  get needsAltText() {
    return notEmptyString(this.image, 5);
  }

  get targetSize() {
    switch (this.type) {
      case 'biography':
        return 'featured';
      default:
        return 'thumb';
    }
  }

  get formatNotes() {
    const sn = this.site.snippets.find(sn => sn.key === 'submission__featured_image_size_notes');
    const text = sn instanceof Object ? sn.value : "Masked areas will be cropped";
    return text;
  }

  update(removeImage = false) {
    const statement = this.submission.statements.find(
      (st) => st.type === this.type
    );
    if (statement) {
      statement.text = highlightTags(cleanFromWysiwyg(this.text));
      statement.alt = this.alt;
      if (removeImage) {
        statement.image = "";
      }
    } else {
      this.submission.statements.push(
        new Statement({
          type: this.type,
          text: highlightTags(this.text),
          alt: this.alt,
          image: this.image,
        })
      );
    }
  }

  confirmRemove() {
    const message = "Do you want to remove this image?";
    this.$buefy.dialog.confirm({
      message,
      type: "is-danger",
      onConfirm: () => this.removeImage(),
    });
  }

  removeImage() {
    this.image = "";
    this.update(true);
  }

  get refKey() {
    switch (this.type) {
      case "where_am_i":
        return "statement";
      case "biography":
        return "featured";
      default:
        return "-";
    }
  }

  get imageHelpKey() {
    return [this.type, 'image', 'help'].join("_");
  }

  htmlChange(delta, source) {
    this.text = textChange(delta, source, this.text);
  }

  loadUploadForm() {
    const refKey = [this.type, this.refKey].join("__");
    bus.$emit("upload-ref", { refKey, type: "statement" });
  }

  setUploadRef() {
    this.loadUploadForm();
    /* const exceptClasses = ["mdi-trash-can-outline", "remove", "control", "alt-text", "input", "help-widget", "icon", "mdi", "textarea","image-actions"];
    if (e) {
        const tg = e.target;
      const tn = e.target.tagName.toLowerCase();
      if (['i'].includes(tn) === false && tg.classList.contains('icon') === false) {
        const isException = exceptClasses.some((cl) =>
          e.target.classList.contains(cl)
        );
        if (!isException) {
          this.loadUploadForm();
        }
      }
    } */
  }

  @Watch("text")
  changeText() {
    this.update();
  }

  @Watch("alt")
  changeAlt() {
    this.update();
  }

  @Watch("image")
  changeImage() {
    this.update();
  }
}
